import { useState } from 'react';
import { PencilIcon, EnvelopeIcon, CheckCircleIcon, SignalIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useSettings } from '../../contexts/SettingsContext';
import { testEmailConnection } from '../../lib/sendTestEmail';
import EmailTestButton from './EmailTestButton';
import EmailStatusMonitor from './EmailStatusMonitor';
import EmailProviderFields from './EmailProviderFields';

export type EmailProvider = 'sendgrid' | 'mailgun' | 'postmark' | 'resend' | 'smtp';

export interface EmailProviderConfig {
  provider: EmailProvider;
  from_email: string;
  from_name: string;
  sendgrid?: { api_key: string };
  mailgun?: { api_key: string; domain: string; region: 'us' | 'eu' };
  postmark?: { server_token: string };
  resend?: { api_key: string };
  smtp?: { host: string; port: number; username: string; password: string; secure: boolean };
}

export interface ProviderInfo {
  id: EmailProvider;
  name: string;
  description: string;
  color: string;
}

export const EMAIL_PROVIDERS: ProviderInfo[] = [
  { id: 'sendgrid', name: 'SendGrid', description: 'Email API by Twilio', color: 'bg-blue-600' },
  { id: 'mailgun', name: 'Mailgun', description: 'Email API by Sinch', color: 'bg-red-600' },
  { id: 'postmark', name: 'Postmark', description: 'Transactional email service', color: 'bg-amber-500' },
  { id: 'resend', name: 'Resend', description: 'Modern email API', color: 'bg-gray-900' },
  { id: 'smtp', name: 'SMTP', description: 'Custom SMTP server', color: 'bg-green-600' },
];

const DEFAULT_CONFIG: EmailProviderConfig = {
  provider: 'sendgrid',
  from_email: '',
  from_name: '',
  sendgrid: { api_key: '' },
  mailgun: { api_key: '', domain: '', region: 'us' },
  postmark: { server_token: '' },
  resend: { api_key: '' },
  smtp: { host: '', port: 587, username: '', password: '', secure: true },
};

export default function EmailSettingsForm() {
  const { state, updateSystemSetting, addSystemSetting } = useSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<EmailProviderConfig>(DEFAULT_CONFIG);
  const [testEmail, setTestEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [connectionTest, setConnectionTest] = useState<{ testing: boolean; result?: { success: boolean; message: string } }>({ testing: false });

  const existingSetting = state.systemSettings.find(s => s.key === 'email_provider_config');
  const legacySendgrid = state.systemSettings.find(s => s.key === 'sendgrid_settings');
  const legacySmtp = state.systemSettings.find(s => s.key === 'smtp_settings');

  const getSavedConfig = (): EmailProviderConfig | null => {
    if (existingSetting?.value?.provider) {
      return existingSetting.value as EmailProviderConfig;
    }
    if (legacySendgrid?.value?.api_key) {
      return {
        provider: 'sendgrid',
        from_email: legacySendgrid.value.from_email || '',
        from_name: legacySendgrid.value.from_name || '',
        sendgrid: { api_key: legacySendgrid.value.api_key },
      };
    }
    if (legacySmtp?.value?.host) {
      return {
        provider: 'smtp',
        from_email: legacySmtp.value.from_email || '',
        from_name: legacySmtp.value.from_name || '',
        smtp: {
          host: legacySmtp.value.host,
          port: legacySmtp.value.port || 587,
          username: legacySmtp.value.username || '',
          password: legacySmtp.value.password || '',
          secure: legacySmtp.value.secure ?? true,
        },
      };
    }
    return null;
  };

  const savedConfig = getSavedConfig();

  const handleEdit = () => {
    const base = savedConfig || DEFAULT_CONFIG;
    setConfig({
      ...DEFAULT_CONFIG,
      ...base,
      sendgrid: { ...DEFAULT_CONFIG.sendgrid!, ...base.sendgrid },
      mailgun: { ...DEFAULT_CONFIG.mailgun!, ...base.mailgun },
      postmark: { ...DEFAULT_CONFIG.postmark!, ...base.postmark },
      resend: { ...DEFAULT_CONFIG.resend!, ...base.resend },
      smtp: { ...DEFAULT_CONFIG.smtp!, ...base.smtp },
    });
    setErrors({});
    setIsEditing(true);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!config.from_email) newErrors.from_email = 'From email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.from_email)) newErrors.from_email = 'Invalid email format';
    if (!config.from_name) newErrors.from_name = 'From name is required';

    switch (config.provider) {
      case 'sendgrid':
        if (!config.sendgrid?.api_key) newErrors.sendgrid_api_key = 'API key is required';
        break;
      case 'mailgun':
        if (!config.mailgun?.api_key) newErrors.mailgun_api_key = 'API key is required';
        if (!config.mailgun?.domain) newErrors.mailgun_domain = 'Domain is required';
        break;
      case 'postmark':
        if (!config.postmark?.server_token) newErrors.postmark_server_token = 'Server token is required';
        break;
      case 'resend':
        if (!config.resend?.api_key) newErrors.resend_api_key = 'API key is required';
        break;
      case 'smtp':
        if (!config.smtp?.host) newErrors.smtp_host = 'SMTP host is required';
        if (!config.smtp?.port) newErrors.smtp_port = 'Port is required';
        if (!config.smtp?.username) newErrors.smtp_username = 'Username is required';
        if (!config.smtp?.password) newErrors.smtp_password = 'Password is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      if (existingSetting) {
        await updateSystemSetting({ ...existingSetting, value: config });
      } else {
        await addSystemSetting({
          key: 'email_provider_config',
          value: config,
          category: 'email',
          description: 'Email provider configuration',
          is_public: false,
        });
      }
      setIsEditing(false);
      toast.success('Email configuration saved');
    } catch {
      toast.error('Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setConnectionTest({ testing: true, result: undefined });
    try {
      const result = await testEmailConnection();
      setConnectionTest({ testing: false, result });
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch {
      setConnectionTest({ testing: false, result: { success: false, message: 'Connection test failed unexpectedly' } });
      toast.error('Connection test failed');
    }
  };

  const activeProvider = EMAIL_PROVIDERS.find(p => p.id === savedConfig?.provider);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-golf-gray">Email Settings</h2>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center space-x-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
          >
            <PencilIcon className="w-5 h-5" />
            <span>Edit Settings</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-golf-gray mb-4">Select Email Provider</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {EMAIL_PROVIDERS.map((provider) => (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => setConfig(prev => ({ ...prev, provider: provider.id }))}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    config.provider === provider.id
                      ? 'border-golf-orange bg-golf-orange/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${provider.color} flex items-center justify-center text-white font-bold text-sm shrink-0`}>
                      {provider.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        {provider.name}
                        {config.provider === provider.id && (
                          <CheckCircleIcon className="w-4 h-4 text-golf-orange shrink-0" />
                        )}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{provider.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-golf-gray mb-4 flex items-center gap-2">
              <EnvelopeIcon className="w-5 h-5 text-golf-orange" />
              Sender Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
                <input
                  type="email"
                  value={config.from_email}
                  onChange={e => setConfig(prev => ({ ...prev, from_email: e.target.value }))}
                  className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.from_email ? 'border-red-300' : ''}`}
                  placeholder="noreply@example.com"
                />
                {errors.from_email && <p className="mt-1 text-sm text-red-600">{errors.from_email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                <input
                  type="text"
                  value={config.from_name}
                  onChange={e => setConfig(prev => ({ ...prev, from_name: e.target.value }))}
                  className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.from_name ? 'border-red-300' : ''}`}
                  placeholder="Ibom Open"
                />
                {errors.from_name && <p className="mt-1 text-sm text-red-600">{errors.from_name}</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <EmailProviderFields config={config} setConfig={setConfig} errors={errors} />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Email Provider</h3>
                {activeProvider ? (
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${activeProvider.color} flex items-center justify-center text-white font-bold text-xs`}>
                      {activeProvider.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{activeProvider.name}</p>
                      <p className="text-sm text-gray-500">{activeProvider.description}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-lg font-semibold text-gray-400">Not configured</p>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Sender Information</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {savedConfig?.from_name || 'Not configured'}
                </p>
                <p className="text-sm text-gray-500">
                  {savedConfig?.from_email || 'No email configured'}
                </p>
              </div>
            </div>

            {savedConfig && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Configuration Details</h3>
                <ProviderConfigSummary config={savedConfig} />
              </div>
            )}

            {!savedConfig && (
              <div className="text-center py-8 text-gray-500">
                <EnvelopeIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No email configuration found.</p>
                <p className="text-sm mt-1">Click "Edit Settings" to configure an email provider.</p>
              </div>
            )}
          </div>

          {savedConfig && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-md font-medium text-golf-gray mb-4">Verify Connection</h3>
              <p className="text-sm text-gray-500 mb-4">
                Test that your {activeProvider?.name || 'email provider'} credentials are valid and the service is reachable.
              </p>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleTestConnection}
                  disabled={connectionTest.testing}
                  className="flex items-center gap-2 px-4 py-2 bg-golf-green text-white font-medium rounded-lg hover:bg-golf-green/90 transition-colors disabled:opacity-50"
                >
                  <SignalIcon className="w-5 h-5" />
                  {connectionTest.testing ? 'Testing...' : 'Test Connection'}
                </button>
                {connectionTest.result && (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                    connectionTest.result.success
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {connectionTest.result.success ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                    ) : (
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600 shrink-0" />
                    )}
                    {connectionTest.result.message}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <h3 className="text-md font-medium text-golf-gray">Send Test Email</h3>
              <div className="flex items-center gap-4">
                <input
                  type="email"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                <EmailTestButton recipientEmail={testEmail} />
              </div>
            </div>
            <div className="mt-6">
              <EmailStatusMonitor />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProviderConfigSummary({ config }: { config: EmailProviderConfig }) {
  const rows: { label: string; value: string }[] = [];

  switch (config.provider) {
    case 'sendgrid':
      if (config.sendgrid?.api_key) rows.push({ label: 'API Key', value: maskSecret(config.sendgrid.api_key) });
      break;
    case 'mailgun':
      if (config.mailgun?.api_key) rows.push({ label: 'API Key', value: maskSecret(config.mailgun.api_key) });
      if (config.mailgun?.domain) rows.push({ label: 'Domain', value: config.mailgun.domain });
      if (config.mailgun?.region) rows.push({ label: 'Region', value: config.mailgun.region.toUpperCase() });
      break;
    case 'postmark':
      if (config.postmark?.server_token) rows.push({ label: 'Server Token', value: maskSecret(config.postmark.server_token) });
      break;
    case 'resend':
      if (config.resend?.api_key) rows.push({ label: 'API Key', value: maskSecret(config.resend.api_key) });
      break;
    case 'smtp':
      if (config.smtp?.host) rows.push({ label: 'Host', value: config.smtp.host });
      if (config.smtp?.port) rows.push({ label: 'Port', value: String(config.smtp.port) });
      if (config.smtp?.username) rows.push({ label: 'Username', value: config.smtp.username });
      rows.push({ label: 'Password', value: maskSecret('hidden') });
      rows.push({ label: 'TLS/SSL', value: config.smtp?.secure ? 'Enabled' : 'Disabled' });
      break;
  }

  if (rows.length === 0) return null;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((row, i) => (
          <div key={i}>
            <p className="text-sm text-gray-500">{row.label}</p>
            <p className="text-sm font-medium text-gray-900">{row.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function maskSecret(value: string): string {
  if (value.length <= 8) return '\u2022'.repeat(12);
  return value.slice(0, 4) + '\u2022'.repeat(16) + value.slice(-4);
}
